/*
 * TTTech nerve-management-system-frontend
 * Copyright(c) 2026 TTTech Industrial Automation AG.
 *
 * ALL RIGHTS RESERVED.
 * Usage of this software, including source code, netlists, documentation,
 * is subject to restrictions and conditions of the applicable license
 * agreement with TTTech Industrial Automation AG or its affiliates.
 *
 * All trademarks used are the property of their respective owners.
 * TTTech Industrial Automation AG and its affiliates do not assume any liability
 * arising out of the application or use of any product described or shown
 * herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 * make changes, at any time, in order to improve reliability, function or
 * design.
 *
 * Contact Information:
 * support@tttech-industrial.com
 * TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 */

import { defineStore } from 'pinia';
import { LdapApiService } from '@/services/api';
import LdapModel from '@/model/local-repository/ldap/ldap.model';

import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';

const useLdapStore = defineStore('ldap', {
  state: () => ({
    defaultLdap: new LdapModel(),
    connectionTestResult: null,
    validGroups: [],
    validUsers: [],
    isLdapActive: false,
    // Request in Progress flags
    isTestingConnection: false,
    isSaving: false,
    isSyncing: false,
    isActivatingLdap: false,
  }),

  actions: {
    _showCustomToast(text, color = 'success', showClose = true) {
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text,
        color,
        showClose,
      });
    },

    async getDefaultLdap() {
      try {
        const defaultLdap = await LdapApiService.getDefaultLdap();
        const ldap = new LdapModel(defaultLdap);

        // If recurring synchronization is disabled, reset the recurringSync object
        // to only include the recurringSync flag set to false, ensuring unnecessary fields
        // like time and schedule are not sent to the backend.
        if (!ldap.recurringSync?.recurringSync) {
          ldap.recurringSync = { recurringSync: false };
        }

        this.defaultLdap = ldap;
      } catch (e) {
        this.defaultLdap = new LdapModel();
        throw e;
      }
    },

    async createLdap(ldap) {
      try {
        this.isSaving = true;

        const response = await LdapApiService.create(ldap);
        Object.assign(this.defaultLdap, response);

        this._showCustomToast('ldap.createLdapMessage');
      } finally {
        this.isSaving = false;
      }
    },

    async updateLdap(ldap) {
      try {
        this.isSaving = true;

        await LdapApiService.update(ldap);
        Object.assign(this.defaultLdap, ldap);

        localStorage.setItem('isLdapActive', ldap.active);

        this._showCustomToast('ldap.updateLdapMessage');
      } finally {
        this.isSaving = false;
      }
    },

    async testGroupsSync(parameters = {}) {
      try {
        const res = await LdapApiService.testGroupsSync({ ldap: parameters.ldap });
        this.validGroups = res.groups;

        return res;
      } catch (e) {
        this.validGroups = [];
        throw e;
      }
    },

    async testUsersSync(parameters = {}) {
      try {
        const res = await LdapApiService.testUsersSync({ ldap: parameters.ldap });
        this.validUsers = res.users;

        return res;
      } catch (e) {
        this.validUsers = [];
        throw e;
      }
    },

    async testConnection(ldap) {
      try {
        this.isTestingConnection = true;

        const res = await LdapApiService.testConnection(ldap);
        this.connectionTestResult = res.connected;
      } finally {
        this.isTestingConnection = false;
      }
    },

    async sync(ldap) {
      try {
        this.isSyncing = true;
        await LdapApiService.sync(ldap);

        this._showCustomToast('ldap.syncLdapMessage');
      } finally {
        this.isSyncing = false;
      }
    },

    async changeLdapActivation(ldap) {
      try {
        this.isActivatingLdap = true;

        await LdapApiService.setActiveLdap({ name: ldap.fileName, active: ldap.active });
        this.defaultLdap.active = ldap.active;

        localStorage.setItem('isLdapActive', ldap.active);

        this._showCustomToast(ldap.active ? 'ldap.LdapActivated' : 'ldap.LdapDeactivated');
      } catch (e) {
        this.defaultLdap.active = !ldap.active;
        throw e;
      } finally {
        this.isActivatingLdap = false;
      }
    },

    async getLdapStatus() {
      const response = await LdapApiService.getActiveLdap();
      this.isLdapActive = response.active;
    },
  },
});

export default useLdapStore;
